import {
  useTimeline,
  TimelineBox,
  TimelineWrapper,
  ProgramItem,
  useProgram,
  ProgramBox,
  ProgramContent,
  Timeline,
  ChannelBox,
  ChannelItem,
  GridItem,
  GridDivider,
  GridCell as GridCellProps,
} from '@interaction-gaming/planby'
import './style.scss'
import styled from '@emotion/styled'

import { Em, Flex, HoverCard, Text } from '@radix-ui/themes'
import { FileTextIcon } from '@radix-ui/react-icons'
import { Bookable, GridDisplayMode, ReservationGridDisplayItem } from './types'
import { useCallback } from 'react'
import CheckedInIcon from '@Assets/icons/checked-in.svg'
import NoShowIcon from '@Assets/icons/no-show.svg'

type CustomTimelineProps = Timeline & {
  isWeekView: boolean
}

export function CustomTimeline(props: CustomTimelineProps) {
  const { time, timelineHeight, getTime, getTimelineProps } = useTimeline(props)

  const { isToday, isCurrentTime, isVerticalMode, hourWidth } = props

  const getTimeIntervals = useCallback(
    (item: string | number) => {
      const { time } = getTime(item)

      if (props.isWeekView) {
        return (
          <Flex direction="column" className="timeline-box">
            <div className="timeline-box__items-center">{time}</div>
            <div className="timeline-box__items-right">{time.replace('00', '15')}</div>
            <div className="timeline-box__items-right">{time.replace('00', '30')}</div>
            <div className="timeline-box__items-right">{time.replace('00', '45')}</div>
          </Flex>
        )
      }

      return (
        <Flex direction="column" className="timeline-box">
          <div className="timeline-box__items">{time}</div>
          <div className="timeline-box__items">{time.replace('00', '30')}</div>
        </Flex>
      )
    },
    [getTime, props.isWeekView],
  )

  const renderTime = useCallback(
    (item: string | number, index: number) => {
      const position = { left: hourWidth * index, width: hourWidth }

      return (
        <TimelineBox
          key={index}
          isToday={isToday}
          isCurrentTime={isCurrentTime}
          isVerticalMode={isVerticalMode}
          timelineHeight={timelineHeight}
          style={{ left: '9px' }}
          {...position}>
          {getTimeIntervals(item)}
        </TimelineBox>
      )
    },
    [getTimeIntervals, hourWidth, isCurrentTime, isToday, isVerticalMode, timelineHeight],
  )

  return (
    <TimelineWrapper {...getTimelineProps()}>
      {time.map((item, index) => renderTime(item, index))}
    </TimelineWrapper>
  )
}

type ReservationItem = ProgramItem & {
  onSelected: (id: string, isReservation: boolean) => void
  onClaimFormClick?: (reservationId: string) => void
}

const StatusBadge = ({
  checkedInAt,
  noShowAt,
  isWeekView,
}: {
  checkedInAt?: Date | null
  noShowAt?: Date | null
  isWeekView: boolean
}) => {
  if (!checkedInAt && !noShowAt) {
    return null
  }

  const size = isWeekView ? 11 : 13

  if (checkedInAt) {
    return (
      <Flex align="center" gap="1" className="status-badge">
        <img src={CheckedInIcon} width={size} height={size} alt="" />
        <Text size="1" className="status-badge-text-checked">
          Checked In
        </Text>
      </Flex>
    )
  }

  return (
    <Flex align="center" gap="1" className="status-badge">
      <img src={NoShowIcon} width={size} height={size} alt="" />
      <Text size="1" className="status-badge-text-noshow">
        No Show
      </Text>
    </Flex>
  )
}

export const ReservationContent = ({
  reservation,
  sinceTime,
  tillTime,
}: {
  reservation: ReservationGridDisplayItem
  sinceTime: string
  tillTime: string
}) => {
  const detailedText = (
    <Flex gap="2" direction="column">
      <StatusBadge
        checkedInAt={reservation.checkedInAt}
        noShowAt={reservation.noShowAt}
        isWeekView={false}
      />
      <Text className="grid-item-time" size="1">
        {sinceTime} - {tillTime}
      </Text>
      <Flex direction="column">
        <Text weight="bold" size="2">
          {reservation.title}
        </Text>
        <Text>{reservation.description}</Text>
      </Flex>
      <Text>{reservation.subDescription}</Text>
    </Flex>
  )

  return reservation.gridDisplayMode === GridDisplayMode.WEEK ? (
    <HoverCard.Root>
      <HoverCard.Trigger>
        <Flex direction="column" gap="1">
          <StatusBadge
            checkedInAt={reservation.checkedInAt}
            noShowAt={reservation.noShowAt}
            isWeekView={true}
          />
          <Text weight="bold" size="1" truncate>
            {reservation.title}
          </Text>
        </Flex>
      </HoverCard.Trigger>
      <HoverCard.Content size="1" minWidth="290px" side="top" align="center" className="hover-card">
        {detailedText}
      </HoverCard.Content>
    </HoverCard.Root>
  ) : (
    detailedText
  )
}

export const ExceptionContent = ({
  exception,
  sinceTime,
  tillTime,
}: {
  exception: ReservationGridDisplayItem
  sinceTime: string
  tillTime: string
}) => {
  const detailedText = (
    <Flex gap="2" direction="column">
      <Text className="grid-item-time" size="1">
        <Em>
          {sinceTime} - {tillTime}
        </Em>
      </Text>
      <Flex direction="column">
        <Text className="grid-exception-item-text" size="2">
          <Em>{exception.title}</Em>
        </Text>
        <Text className="grid-exception-item-text">
          <Em>{exception.description}</Em>
        </Text>
      </Flex>
    </Flex>
  )

  return exception.gridDisplayMode === GridDisplayMode.WEEK ? (
    <HoverCard.Root>
      <HoverCard.Trigger>
        <Text size="1" truncate>
          <Em>Unavailable</Em>
        </Text>
      </HoverCard.Trigger>
      <HoverCard.Content
        size="1"
        minWidth="290px"
        side="right"
        align="center"
        className="hover-card">
        {detailedText}
      </HoverCard.Content>
    </HoverCard.Root>
  ) : (
    detailedText
  )
}

const StyledProgramContent = styled(ProgramContent)<{
  condensed: boolean
  isReservation: boolean
}>(({ condensed, isReservation }) => ({
  border: 'none',
  ...(condensed
    ? {
        padding: '0',
        background: isReservation ? 'rgba(165, 239, 255, 0.40)' : 'rgba(233, 233, 233, 0.40)',
        borderRadius: '4px',
        ':hover': {
          background: isReservation ? 'rgba(165, 239, 255, 0.80)' : 'rgba(233, 233, 233, 0.80)',
        },
      }
    : {}),
}))

const StyledProgramBox = styled(ProgramBox)<{ condensed: boolean }>(({ condensed }) => ({
  ...(condensed ? {} : { borderRadius: '8px', background: 'rgba(255, 255, 255, 0.40)' }),
}))

export const ReservationGridItem = ({
  program,
  onSelected,
  onClaimFormClick,
  ...rest
}: ReservationItem) => {
  const { styles, isLive } = useProgram({
    program,
    ...rest,
  })

  const { data } = program
  const {
    formattedStartTime,
    formattedEndTime,
    isReservation,
    gridDisplayMode,
    claimFormId,
    claimFormAuthorized,
  } = data as ReservationGridDisplayItem

  if (data.id.split(':')[0] === 'filler') {
    return null
  }

  const isWeekView = gridDisplayMode === GridDisplayMode.WEEK

  return (
    <StyledProgramBox
      width={styles.width}
      style={{
        ...styles.position,
        top: isWeekView ? styles.position.top + 8 : styles.position.top,
      }}
      onClick={() => {
        onSelected(data.id, isReservation)
      }}
      condensed={isWeekView}>
      <StyledProgramContent
        width={styles.width}
        isLive={isLive}
        className={
          isReservation
            ? 'grid-reservation'
            : isWeekView
              ? 'grid-exception-week-view'
              : 'grid-exception'
        }
        condensed={isWeekView}
        isReservation={isReservation}>
        <Flex
          className={isWeekView ? 'condensed-grid-item' : 'grid-item'}
          style={{ position: 'relative' }}>
          {isReservation && claimFormId && onClaimFormClick ? (
            <button
              className={`claim-form-icon ${isWeekView ? 'week' : ''} ${claimFormAuthorized ? 'authorized' : ''}`}
              onClick={(e) => {
                e.stopPropagation()
                onClaimFormClick(data.id)
              }}
              aria-label="View claim form">
              <FileTextIcon />
            </button>
          ) : null}
          {isReservation ? (
            <ReservationContent
              reservation={data as ReservationGridDisplayItem}
              sinceTime={formattedStartTime}
              tillTime={formattedEndTime}
            />
          ) : (
            <ExceptionContent
              exception={data as ReservationGridDisplayItem}
              sinceTime={formattedStartTime}
              tillTime={formattedEndTime}
            />
          )}
        </Flex>
      </StyledProgramContent>
    </StyledProgramBox>
  )
}

const StyledChannelBox = styled(ChannelBox)({
  background: 'none',
  justifyContent: 'start',
  fontFamily: 'Mullish',
  fontWeight: 700,
})

export const CustomChannelItem = ({ isVerticalMode, channel }: ChannelItem) => {
  const { position, gridDisplayMode, formattedDay } = channel as Bookable
  return gridDisplayMode === GridDisplayMode.DAY ? null : (
    <StyledChannelBox isVerticalMode={isVerticalMode} {...position}>
      <Text className="week-day-label">{formattedDay}</Text>
    </StyledChannelBox>
  )
}

export function GridCell(props: GridCellProps) {
  const {
    isDayMode,
    isHoverHighlight,
    item,
    timelineDividerArray,
    gridDividerProps,
    gridItemClickProps,
  } = props

  const { onItemClick, ...dividerProps } = gridDividerProps.props
  const { left, ...styles } = gridDividerProps.styles
  return (
    <GridItem
      className={`grid-cell-item ${item.channel.isToday && item.channel.gridDisplayMode === GridDisplayMode.WEEK ? 'grid-cell-item-bold' : ''}`}
      isDayMode={isDayMode}
      isHoverHighlight={isHoverHighlight as boolean}
      {...item.position}
      {...gridItemClickProps}>
      {isDayMode &&
        timelineDividerArray.map((_, index) => (
          <GridDivider
            className="grid-cell-item-divider"
            key={index}
            {...styles}
            {...dividerProps}
            left={left(index) + 8}
            onClick={onItemClick(item, index)}></GridDivider>
        ))}
    </GridItem>
  )
}
